import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import { Copy, Download, Loader2, FileCode2 } from "lucide-react";

const PLACEHOLDER = "// Genera il codice configurando i parametri…";

const LINE_HEIGHT = 20; // px per row (must match the row style below)
const FONT_SIZE = 12.5;
const CHAR_W = 7.55; // approx monospace glyph width @12.5px IBM Plex Mono
const GUTTER_W = 56; // line-number gutter width (px)
const OVERSCAN = 14; // extra rows rendered above/below the viewport

// --- lightweight C# tokenizer (per visible line only) ---------------------
const KEYWORDS = new Set([
  "public", "private", "protected", "internal", "static", "void", "int",
  "double", "bool", "string", "var", "new", "return", "if", "else", "switch",
  "case", "default", "break", "continue", "for", "foreach", "while", "do",
  "class", "namespace", "using", "get", "set", "null", "true", "false", "this",
  "override", "virtual", "abstract", "readonly", "const", "enum", "struct",
  "in", "out", "ref", "try", "catch", "finally", "throw", "base", "is", "as",
  "long", "float", "decimal", "object", "sealed", "partial", "async", "await",
  "when", "params", "typeof", "nameof", "operator", "explicit", "implicit",
]);
const TYPES = new Set([
  "Position", "TradeType", "Color", "TextBlock", "StackPanel", "Border",
  "DateTime", "DateTimeOffset", "TimeSpan", "MarketRegime", "DdGrade", "Symbol",
  "Math", "Server", "Account", "Chart", "Positions", "HashSet", "List",
  "Dictionary", "JsonDocument", "JsonElement", "HttpClient", "Exception",
  "CultureInfo", "DateTimeStyles", "Orientation", "FontWeight",
  "HorizontalAlignment", "VerticalAlignment", "Thickness", "Robot", "Bars",
  "StringSplitOptions",
]);

const COLORS = {
  cm: "#5f7e6b",
  st: "#b6e3a7",
  kw: "#57d9a3",
  ty: "#8fe388",
  num: "#e8c07d",
  id: "#d6f5e3",
  pl: "#9fc7ad",
};

function tokenizeLine(line) {
  const tokens = [];
  let i = 0;
  const n = line.length;
  let buf = "";

  const pushCode = (chunk) => {
    const re = /([A-Za-z_][A-Za-z0-9_]*)|(\d+\.?\d*)|([^A-Za-z0-9_]+)/g;
    let m;
    while ((m = re.exec(chunk))) {
      if (m[1]) {
        const w = m[1];
        if (KEYWORDS.has(w)) tokens.push({ t: w, c: "kw" });
        else if (TYPES.has(w)) tokens.push({ t: w, c: "ty" });
        else tokens.push({ t: w, c: "id" });
      } else if (m[2]) {
        tokens.push({ t: m[2], c: "num" });
      } else {
        tokens.push({ t: m[3], c: "pl" });
      }
    }
  };
  const flush = () => {
    if (buf) {
      pushCode(buf);
      buf = "";
    }
  };

  while (i < n) {
    const ch = line[i];
    if (ch === "/" && line[i + 1] === "/") {
      flush();
      tokens.push({ t: line.slice(i), c: "cm" });
      i = n;
      break;
    }
    if (ch === '"') {
      flush();
      let j = i + 1;
      while (j < n) {
        if (line[j] === "\\") {
          j += 2;
          continue;
        }
        if (line[j] === '"') {
          j += 1;
          break;
        }
        j += 1;
      }
      tokens.push({ t: line.slice(i, j), c: "st" });
      i = j;
      continue;
    }
    buf += ch;
    i += 1;
  }
  flush();
  return tokens;
}

const CodeRow = React.memo(function CodeRow({ lineNo, text }) {
  const tokens = text.length ? tokenizeLine(text) : null;
  return (
    <div style={{ height: LINE_HEIGHT, display: "flex", alignItems: "stretch" }}>
      <span
        aria-hidden="true"
        style={{
          width: GUTTER_W,
          minWidth: GUTTER_W,
          textAlign: "right",
          paddingRight: 12,
          color: "#4b5763",
          userSelect: "none",
          lineHeight: `${LINE_HEIGHT}px`,
        }}
      >
        {lineNo}
      </span>
      <span style={{ whiteSpace: "pre", lineHeight: `${LINE_HEIGHT}px` }}>
        {tokens
          ? tokens.map((tk, idx) => (
              <span
                key={idx}
                style={{
                  color: COLORS[tk.c] || COLORS.id,
                  fontStyle: tk.c === "cm" ? "italic" : "normal",
                }}
              >
                {tk.t}
              </span>
            ))
          : "\u00A0"}
      </span>
    </div>
  );
});

/**
 * Virtualized C# code viewer. Only the rows visible in the viewport (plus a
 * small overscan) are rendered, so a 2000+ line file no longer produces tens
 * of thousands of DOM nodes — scrolling and re-generation stay smooth.
 */
const VirtualizedCode = React.memo(function VirtualizedCode({ code }) {
  const lines = useMemo(() => (code && code.length ? code : PLACEHOLDER).split("\n"), [code]);
  const maxLen = useMemo(() => {
    let m = 0;
    for (let k = 0; k < lines.length; k += 1) if (lines[k].length > m) m = lines[k].length;
    return m;
  }, [lines]);

  const scrollRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportH, setViewportH] = useState(700);
  const rafRef = useRef(0);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const measure = () => setViewportH(el.clientHeight || 700);
    measure();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    return () => {
      if (ro) ro.disconnect();
    };
  }, []);

  const onScroll = useCallback((e) => {
    const top = e.target.scrollTop;
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      setScrollTop(top);
    });
  }, []);

  const total = lines.length * LINE_HEIGHT;
  const contentWidth = Math.max(GUTTER_W + maxLen * CHAR_W + 32, 320);
  const start = Math.max(0, Math.floor(scrollTop / LINE_HEIGHT) - OVERSCAN);
  const end = Math.min(
    lines.length,
    Math.ceil((scrollTop + viewportH) / LINE_HEIGHT) + OVERSCAN
  );
  const visible = lines.slice(start, end);

  return (
    <div
      ref={scrollRef}
      onScroll={onScroll}
      className="code-scroll overflow-auto"
      style={{ height: "min(70vh, 720px)" }}
      data-testid="csharp-code-preview"
    >
      <div
        style={{
          position: "relative",
          height: total,
          width: contentWidth,
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: FONT_SIZE,
          padding: "8px 0",
        }}
      >
        <div style={{ position: "absolute", top: start * LINE_HEIGHT, left: 16, right: 0 }}>
          {visible.map((ln, idx) => (
            <CodeRow key={start + idx} lineNo={start + idx + 1} text={ln} />
          ))}
        </div>
      </div>
    </div>
  );
});

export const CodePreview = ({
  code,
  fileName,
  lineCount,
  onCopy,
  onDownload,
  loading,
}) => {
  // Defer the heavy work so it never blocks typing in other inputs.
  const deferredCode = React.useDeferredValue(code);
  return (
    <div
      className="flex flex-col rounded-[14px] border shadow-[var(--shadow-md)] overflow-hidden"
      style={{ borderColor: "var(--code-border)", backgroundColor: "var(--code-bg)" }}
      data-testid="code-preview-container"
    >
      <div
        className="flex items-center justify-between gap-3 px-4 py-3 border-b"
        style={{ borderColor: "var(--code-border)", backgroundColor: "var(--code-surface)" }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <FileCode2 className="h-4 w-4 shrink-0" style={{ color: "var(--code-accent)" }} />
          <div className="min-w-0">
            <div className="text-sm font-medium truncate" style={{ color: "var(--code-text)" }}>
              Anteprima Codice C#
            </div>
            <div className="text-xs font-mono truncate" style={{ color: "var(--code-muted)" }}>
              {fileName || "Bot.cs"} · {lineCount || 0} righe
              {loading ? " · aggiornamento…" : " · aggiornato"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            data-testid="copy-code-button"
            size="sm"
            variant="secondary"
            onClick={onCopy}
            className="bg-white/10 text-white hover:bg-white/20 border-0"
          >
            <Copy className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Copia</span>
          </Button>
          <Button
            data-testid="download-cs-button"
            size="sm"
            onClick={onDownload}
            className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 sm:mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 sm:mr-2" />
            )}
            <span className="hidden sm:inline">Scarica .cs</span>
          </Button>
        </div>
      </div>

      <VirtualizedCode code={deferredCode} />
    </div>
  );
};
